import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  AptunoButton,
  Icon,
  SIZES,
  useResponsive,
  VARIANTS,
} from '@aptuno/aptuno-ui';
import { LineBox, SkeletonLineStyled } from '@components/commons';
import {
  ApplicantType,
  ApplicationStateSteps,
  estateApplicationStageContext,
  getMainTags,
  PropertyCharacteristic,
  PropertyMedia,
  useEstateActions,
} from '@contexts/Estate';
import { getMainImage, parseImages, useTrackingSource } from '@shared/utils';
import { CompactEstateCard } from '../CompactEstateCard/CompactEstateCard';
import {
  CompactEstateCardStyled,
  InfoBox,
} from '../CompactEstateCard/CompactEstateCard.styled';
import {
  ActionsBarRoot,
  Blue,
  HeaderButton,
  TitleSection,
} from '../EstateApplication.styled';
import {
  ApplicationOnboardingWrapper,
  DescriptionSection,
  TagBox,
} from './EstateApplicationOnboarding.styled';
import { useAnalyticsTracker } from '@services/analytics';
import { ApplicationStarted } from '@contexts/Estate/EstateApplication/domain/events/application-started.event';

const EstateApplicationOnboarding = () => {
  const router = useRouter();
  const errorPageUrl = '/404';

  const originSource = useTrackingSource();

  useEffect(() => {
    if (!!router.query?.estateLegalId) {
      getEstateListingDetails({
        estateId: router.query.estateLegalId.toString(),
      });
    }
  }, [router.query?.estateLegalId]);

  const estateApplication = useContext(estateApplicationStageContext);
  const [mainFeatures, setMainFeatures] = useState<PropertyCharacteristic[]>(
    []
  );
  const [mainImage, setMainImage] = useState<PropertyMedia | undefined>(
    undefined
  );
  const { isMobile } = useResponsive();
  const {
    estateListingDetails: estateDetailsData,
    error: estateteDetailsError,
    getEstateListingDetails,
  } = useEstateActions().listingDetailsQueryActions.useGetEstateListingDetails();

  useEffect(() => {
    if (estateteDetailsError) router.push(errorPageUrl);
  }, [estateteDetailsError]);

  useEffect(() => {
    if (!!estateDetailsData) {
      setMainFeatures(getMainTags(estateDetailsData.characteristics));
      setMainImage(getMainImage(estateDetailsData.media));
    }
  }, [estateDetailsData]);

  const handleNextStepClick = () => {
    segmentTrack();
    estateApplication &&
      estateApplication.setCurrentState(ApplicationStateSteps.documentsCheck);
  };
  const eventTracker = useAnalyticsTracker();

  const segmentTrack = () => {
    const userType =
      router && router.query.applicationId
        ? ApplicantType.COTENANT
        : ApplicantType.TENANT;
    const estateLegalId = router && router.query.estateLegalId;

    eventTracker.trackEvent(
      new ApplicationStarted(originSource, userType, `${estateLegalId}`)
    );
  };

  const handleOnBackClick = () => {
    router.push('/aplicar-inmueble/resultados-aplicacion');
  };

  return (
    <ApplicationOnboardingWrapper>
      <HeaderButton
        appearance={VARIANTS.SECONDARY}
        isCircular
        onClick={handleOnBackClick}
      >
        <Icon icon="arrowleftalt" />
      </HeaderButton>
      <TitleSection>
        Tu próximo hogar
        <Blue>te está esperando.</Blue>
      </TitleSection>
      {estateDetailsData && (
        <CompactEstateCard
          imageUrl={parseImages(mainImage?.uri, 'thumb', 'jpg')}
          title={estateDetailsData.title}
          price={estateDetailsData.price || 0}
          administatrionFee={estateDetailsData.administrationPrice || 0}
          rooms={Number(
            mainFeatures.find((feature) => feature.slug === 'nro-cuartos')
              ?.value || '0'
          )}
          bathrooms={Number(
            mainFeatures.find((feature) => feature.slug === 'nro-banos')
              ?.value || '0'
          )}
          squareMeters={Number(
            mainFeatures.find((feature) => feature.slug === 'area')?.value ||
              '0'
          )}
        />
      )}
      {!estateDetailsData && (
        <CompactEstateCardStyled>
          <LineBox>
            <SkeletonLineStyled
              width={isMobile ? '88px' : '120px'}
              height={isMobile ? '88px' : '120px'}
              borderRadius={'1.5rem'}
            />
          </LineBox>
          <InfoBox>
            <SkeletonLineStyled
              width={isMobile ? '120px' : '200px'}
              height={'24px'}
              borderRadius={'.5rem'}
              margin={'5px 0'}
            />
            <SkeletonLineStyled
              width={isMobile ? '100px' : '120px'}
              height={'20px'}
              borderRadius={'.5rem'}
              margin={'5px 0'}
            />
            <SkeletonLineStyled
              width={isMobile ? '120px' : '190px'}
              height={'24px'}
              borderRadius={'.5rem'}
              margin={'5px 0'}
            />
          </InfoBox>
        </CompactEstateCardStyled>
      )}
      <DescriptionSection>
        <p>
          A continuación te solicitaremos algunos documentos para verificar tu
          identidad, ingresos e historial crediticio.
        </p>
      </DescriptionSection>
      <ActionsBarRoot>
        <AptunoButton
          appearance={VARIANTS.PRIMARY}
          size={SIZES.BIGGEST}
          onClick={handleNextStepClick}
        >
          Revisar Documentación{' '}
          <Icon width={'12px'} height={'12px'} icon="Arrow_Right" />
        </AptunoButton>
      </ActionsBarRoot>
    </ApplicationOnboardingWrapper>
  );
};

export { EstateApplicationOnboarding };
